import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { useUserPermission } from '@contexts/UserContext';

import usePlaceholderImageFixed from '@hooks/Utils/UsePlaceholderImageFixed';

import ModelDetailsCover from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsCover';
import ModelDetailsHeaderAgencies from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsHeaderAgencies';
import ModelDetailsHeaderFeatures from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsHeaderFeatures';

import ContactAlert from '@components/ContactAlert/ContactAlert';
import ContactDetailsBankAccounts from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsBankAccounts';
import ContactDetailsHeaderAnagraphical from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderAnagraphical';
import ContactDetailsHeaderLegal from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderLegal';
import ContactHeaderBillingInfos from '@components/ContactDetails/ContactDetailsHeader/ContactHeaderBillingInfos';
import ModelDetailsHeaderContents from '@components/ContactDetails/ContactDetailsHeader/ModelDetailsHeaderContents';
import ContactConnectionDrawer from '@components/Connection/ContactConnectionDrawer';
import ContactUsers from '@components/ContactUsers/ContactUsers';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { MailLine, PhoneLine } from '@components/Gui/NwContactInfo';
import NwIcon from '@components/Gui/NwIcon';
import ModelInTown from '@components/ModelInTown/ModelInTown';
import ContactSocials from '@components/Social/ContactSocials';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    margin: -0.5rem -1rem 0;
    padding: 1.5rem 1rem;
    color: ${(props) => props.theme.TextColor};
    background: ${(props) => props.theme.LayoutBodyBackground};

    .model-details-mobile-header {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 1.1rem;
        padding: 1rem;
        overflow: hidden;
        border: 0;
        border-radius: 6px;
        background: #fff;

        .model-details-mobile-header-cover {
            position: relative;
            flex: 0 0 6rem;
            width: 6rem;
            height: 7rem;

            .ant-spin-nested-loading,
            .header-image-container {
                overflow: hidden;
                border-radius: 6px;
            }

            .ant-spin-nested-loading {
                width: 6rem;
                height: 7rem;
                padding: 0;
                background: transparent;
                box-shadow: 0 2px 8px rgba(31, 48, 61, 0.12);
            }
        }

        .model-details-mobile-header-info {
            min-width: 0;
            padding-top: 0.15rem;

            .model-name {
                margin-bottom: 0.35rem;
                font-size: clamp(1.35rem, 6vw, 1.7rem);
                font-weight: 600;
                line-height: 1.12;
                letter-spacing: -0.025em;
            }

            .header-body-boxes {
                p {
                    margin-bottom: 0;
                }
            }

            .back-models {
                width: fit-content;
                margin-bottom: 0.4rem;
                color: ${(props) => props.theme.TextColorMuted};
                font-size: 0.75rem;
                cursor: pointer;
            }
        }
    }

    .socials-wrapper {
        margin-top: 1rem;
        padding: 0.75rem 1rem;
        border: 0;
        border-radius: 6px;
        background: #fff;

        .contact-socials-links {
            justify-content: center;
            gap: 1.25rem;

            .contact-social-link {
                .icon-container {
                    font-size: 1.55rem;
                }
            }
        }
    }

    .model-details-mobile-body {
        margin-top: 1rem;
        overflow: hidden;
        border: 0;
        border-radius: 6px;
        background: #fff;

        .model-details-mobile-body-line {
            position: relative;
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 0.85rem;
            min-width: 0;
            margin: 0;
            padding: 1rem;
            border-bottom: ${(props) => props.theme.LighterBorder};

            &:last-child {
                border-bottom: 0;
            }

            > :last-child {
                min-width: 0;
                flex: 1;
            }

            .detail-icon {
                flex: 0 0 2.35rem;
                width: 2.35rem;
                height: 2.35rem;
                padding: 0.55rem;
                border-radius: 11px;
                color: ${(props) => props.theme.TextColorMuted};
                background: transparent;
                font-size: 1.2rem;
            }

            .mobile-full-row-trigger {
                cursor: pointer;

                &::before {
                    position: absolute;
                    z-index: 1;
                    inset: 0;
                    content: '';
                }
            }

            .anagraphical-mobile-details {
                display: flex;
                flex-direction: column;
                gap: 0.45rem;

                > div {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
            }

            .line-container-left {
                width: 100%;
                gap: 0.85rem;

                span {
                    display: inline-flex;
                    flex: 0 0 2.35rem;
                    align-items: center;
                    justify-content: center;
                    width: 2.35rem;
                    height: 2.35rem;
                    border-radius: 11px;
                    color: ${(props) => props.theme.TextColorMuted};
                    background: transparent;
                    font-size: 1.15rem;
                    text-align: center;
                }

                p {
                    min-width: 0;
                    overflow-wrap: anywhere;
                }
            }
        }

        .main-contacts-group {
            border-bottom: ${(props) => props.theme.LighterBorder};

            .main-contacts-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                min-height: 3.25rem;
                padding: 0.75rem 1rem;
                border: 0;
                border-bottom: ${(props) => props.theme.LighterBorder};
                color: ${(props) => props.theme.TextColor};
                background: ${(props) => props.theme.SelectedBackgroundColor};
                font: inherit;
                font-weight: 600;
                text-align: left;
                cursor: pointer;

                .main-contacts-edit {
                    color: ${(props) => props.theme.TextColorMuted};
                }
            }

            .main-contact-line {
                padding: 0.65rem 1rem 0.65rem 2rem;

                .line-container-left {
                    width: 100%;
                    gap: 0.65rem;

                    span {
                        flex-basis: 1.75rem;
                        width: 1.75rem;
                        height: auto;
                        padding: 0;
                        background: transparent;
                        font-size: 1rem;
                    }
                }
            }
        }

        .features-group {
            .features-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                min-height: 3.25rem;
                padding: 0.75rem 1rem;
                border: 0;
                border-bottom: ${(props) => props.theme.LighterBorder};
                color: ${(props) => props.theme.TextColor};
                background: ${(props) => props.theme.SelectedBackgroundColor};
                font: inherit;
                font-weight: 600;
                text-align: left;
                cursor: pointer;

                .features-edit {
                    color: ${(props) => props.theme.TextColorMuted};
                }
            }

            .features-content {
                padding: 0.85rem 1rem 1rem;
            }
        }
    }

    @media screen and (max-width: 360px) {
        .model-details-mobile-header {
            gap: 0.8rem;
            padding: 0.85rem;

            .model-details-mobile-header-cover {
                flex-basis: 5rem;
                width: 5rem;
                height: 6rem;

                .ant-spin-nested-loading {
                    width: 5rem;
                    height: 6rem;
                }

                .header-image-container {
                    width: 5rem;
                    height: 6rem;
                }
            }
        }

        .model-details-mobile-body .model-details-mobile-body-line {
            padding: 0.85rem;
        }
    }
`;

const ModelDetailsMobilePage = ({ currentContact, isDrawer }) => {
    const [IsLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [isModelAgencies, setModelAgencies] = useState(false);
    const [isConnectionsDrawerOpen, setConnectionsDrawerOpen] = useState(false);
    const viewModelConnections = useUserPermission(
        'ViewModelConnections',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );

    const handleBackToModels = () => {
        navigate('/models');
    };

    useEffect(() => {
        if (currentContact) {
            setIsLoading(false);
        }
    }, [currentContact]);

    const [urlbg, isPlaceholder] = usePlaceholderImageFixed(
        currentContact.NetworkAgencies[0].CoverGuid,
        currentContact.SubType
    );

    const handleOpenModelAgencies = () => {
        setModelAgencies(true);
    };

    const handleCloseModelAgencies = () => {
        setModelAgencies(false);
    };

    return currentContact ? (
        IsLoading ? (
            <Loading />
        ) : (
            <Wrapper>
                <div className="model-details-mobile-header">
                    <div className="model-details-mobile-header-cover">
                        <ContactAlert size="small" currentContact={currentContact}>
                            <ModelDetailsCover
                                collapseView
                                urlBg={urlbg}
                                isPlaceholder={isPlaceholder}
                                currentContact={currentContact}
                            />
                        </ContactAlert>
                    </div>
                    <div className="model-details-mobile-header-info">
                        {!isDrawer && (
                            <>
                                <FlexContainer className="back-models" alignItems="center" onClick={handleBackToModels}>
                                    <NwIcon icon={byPrefixAndName.fasl['arrow-left']}></NwIcon>&nbsp;back to models list
                                </FlexContainer>
                                <div className="model-name">{`${currentContact.Name} ${currentContact.Surname}`}</div>
                            </>
                        )}
                        <ModelDetailsHeaderAgencies
                            model={currentContact}
                            onOpenModelAgencies={handleOpenModelAgencies}
                        />
                        <ModelInTown modelID={currentContact.ID} modelInTownSchedule={currentContact.InTownSchedule} />
                        <ContactUsers users={currentContact.Users} contactID={currentContact.ID} />
                    </div>
                </div>
                {currentContact.Socials.length > 0 && (
                    <div className="socials-wrapper">
                        <ContactSocials currentContact={currentContact} />
                    </div>
                )}
                <div className="model-details-mobile-body">
                    <div className="model-details-mobile-body-line">
                        <NwIcon className="detail-icon" icon={byPrefixAndName.fasl['id-card']} />
                        <ContactDetailsHeaderAnagraphical
                            contact={currentContact}
                            mobileFullRow
                            stacked
                        />
                    </div>
                    <div className="model-details-mobile-body-line">
                        <NwIcon className="detail-icon" icon={byPrefixAndName.fasl['passport']} />
                        <ContactDetailsHeaderLegal currentContact={currentContact} mobileFullRow />
                    </div>
                    <div className="model-details-mobile-body-line">
                        <NwIcon className="detail-icon" icon={byPrefixAndName.fasl['file-invoice-dollar']} />
                        <ContactHeaderBillingInfos type="model" currentContact={currentContact} mobileFullRow />
                    </div>
                    <div className="model-details-mobile-body-line">
                        <NwIcon className="detail-icon" icon={byPrefixAndName.fasl['piggy-bank']} />
                        <ContactDetailsBankAccounts currentContact={currentContact} mobileFullRow />
                    </div>
                    <div className="model-details-mobile-body-line">
                        <NwIcon className="detail-icon" icon={byPrefixAndName.fasl['newspaper']} />
                        <ModelDetailsHeaderContents currentContact={currentContact} mobileFullRow />
                    </div>
                    {viewModelConnections && (
                        <div className="main-contacts-group">
                            <button
                                type="button"
                                className="main-contacts-header"
                                onClick={() => setConnectionsDrawerOpen(true)}
                            >
                                <span>Main Contacts</span>
                                <NwIcon className="main-contacts-edit" icon={byPrefixAndName.fasl['pen']} />
                            </button>
                            <div className="main-contact-line">
                                <PhoneLine
                                    noIcon
                                    phoneNumber={currentContact.DefaultConnection?.Phone}
                                    phoneType="Land"
                                />
                            </div>
                            <div className="main-contact-line">
                                <PhoneLine
                                    noIcon
                                    phoneNumber={currentContact.DefaultConnection?.Mobile}
                                    phoneType="Mobile"
                                />
                            </div>
                            <div className="main-contact-line">
                                <MailLine
                                    noIcon
                                    mailAddress={currentContact.DefaultConnection?.Email}
                                    mailCC={currentContact.DefaultConnection?.CC}
                                />
                            </div>
                        </div>
                    )}

                    <div className="features-group">
                        <ModelDetailsHeaderFeatures contact={currentContact} showHeader />
                    </div>
                </div>
                {viewModelConnections && isConnectionsDrawerOpen && (
                    <ContactConnectionDrawer
                        contactId={currentContact.ID}
                        contactSubType={currentContact.SubType}
                        connectionId={currentContact.DefaultConnection?.ID}
                        onClose={() => setConnectionsDrawerOpen(false)}
                    />
                )}
            </Wrapper>
        )
    ) : (
        <Loading />
    );
};

export default ModelDetailsMobilePage;
